<template>
    <v-dialog
        v-model="show"
        persistent
        eager
        :width="displayWidth"
        @keydown.esc="onEscapeKeydown"
    >
        <v-card
            class="scrollable-container pb-2"
            height="450px"
            role="region"
            aria-label="jobs-dialog"
        >
            <v-card-title>
                <div v-if="showDetails">{{ displayTitle }}</div>
                <data-search-bar
                    v-if="!showDetails"
                    :title="title"
                    :options="dataSearchBarOptions"
                    :show-query="false"
                    :cols="{ col3: 7 }"
                    @change="onDataSearchBarChange"
                >
                    <template v-slot:first-column>
                        <v-col>
                            <div>{{ displayTitle }}</div>
                            <div>
                                <v-btn-toggle
                                    v-model="statusFilter"
                                    dense
                                    mandatory
                                    color="accent"
                                    group
                                >
                                    <v-btn value="COMPLETED">Completed</v-btn>
                                    <v-btn value="ALL">All</v-btn>
                                </v-btn-toggle>
                            </div>
                        </v-col>
                    </template>
                    <template v-slot:second-column>
                        <span></span>
                    </template>
                </data-search-bar>
            </v-card-title>
            <v-card-text class="scrollable-content pb-0 px-3">
                <v-slide-x-transition mode="out-in">
                    <data-table-view
                        v-show="!showDetails"
                        :headers="tableFields"
                        :items="jobs"
                        :sort-by="sortBy"
                        :sort-desc="sortDesc"
                        :page="page"
                        :items-per-page="itemsPerPage"
                        :loading="isDataLoading"
                        :zebra-stripping="zebraStripping"
                        :computed-fields="computedFields"
                        :show-select="false"
                        :allow-export="false"
                        @update-sort-by="onUpdateSortBy"
                    >
                        <template v-slot:item.status="{ item }">
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-icon
                                        v-show="item.status === 'COMPLETED'"
                                        large
                                        :color="getJobStatusColor(item)"
                                        class="job-status-icon"
                                        v-on="on"
                                        >{{ getJobStatusIcon(item) }}</v-icon
                                    >
                                    <v-progress-circular
                                        v-show="item.status !== 'COMPLETED'"
                                        indeterminate
                                        size="25"
                                        width="3"
                                        :color="getJobStatusColor(item)"
                                        class="job-status-icon ml-2"
                                        v-on="on"
                                    ></v-progress-circular>
                                </template>
                                <div>
                                    <div>ID: {{ item.id }}</div>
                                    <div>Status: {{ item.status }}</div>
                                    <div>
                                        Tasks: {{ item.expectedTaskCount }}
                                    </div>
                                    <div>Sucess: {{ item.successCount }}</div>
                                    <div>Error: {{ item.errorCount }}</div>
                                </div>
                            </v-tooltip>
                        </template>
                        <template v-slot:tableButtons="{ item }">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-icon
                                        color="accent"
                                        cy-data="view-row"
                                        @click="onJobDetailsClick(item)"
                                        v-on="on"
                                        >{{ icons.moreItems }}
                                    </v-icon>
                                </template>
                                <span class="text-caption">Job details</span>
                            </v-tooltip>
                        </template>
                    </data-table-view>
                </v-slide-x-transition>
                <v-slide-x-reverse-transition mode="in-out">
                    <job-details
                        v-show="showDetails"
                        :job-id="selectedJobId"
                    ></job-details>
                </v-slide-x-reverse-transition>
            </v-card-text>
            <v-card-actions class="justify-end">
                <v-btn
                    v-if="!showDetails"
                    outlined
                    small
                    cy-data="close"
                    @click="onCloseClick"
                    >Close</v-btn
                >
                <v-btn
                    v-if="showDetails"
                    outlined
                    small
                    cy-data="back"
                    @click="onCloseDetailsClick"
                    >Go back</v-btn
                >
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { isEmpty } from '@/common/helpers';
import DataTableView from '@/components/Data/DataTableView';
import DataSearchBar from '@/components/Data/DataSearchBar';
import JobDetails from './JobDetails';

export default {
    name: 'JobsDialog',
    components: {
        'data-table-view': DataTableView,
        'data-search-bar': DataSearchBar,
        'job-details': JobDetails
    },
    props: {
        show: {
            type: Boolean,
            required: true
        },
        title: {
            type: String,
            required: false,
            default: 'Job status'
        },
        width: {
            type: String,
            required: false,
            default: '800'
        }
    },
    data() {
        return {
            isDataLoading: false,
            statusFilter: 'ALL',
            showDetails: false,
            selectedJob: {},
            refreshTimer: null
        };
    },
    computed: {
        ...mapState('jobs', [
            'tableFields',
            'computedFields',
            'filterFields',
            'requestQuery',
            'responseQuery',
            'filterQuery',
            'zebraStripping'
        ]),
        ...mapState('authUser', ['userInfo']),
        ...mapState('jobs', {
            jobs: 'items'
        }),
        displayWidth() {
            if (this.$vuetify.breakpoint.mdAndUp) {
                return this.width;
            }
            return '90%';
        },
        dataSearchBarOptions() {
            return {
                page: this.requestQuery.number,
                itemsPerPage: this.requestQuery.size,
                pageCount: this.responseQuery.totalPages,
                itemsLength: this.responseQuery.totalElements,
                query: this.requestQuery.q
            };
        },
        sortBy() {
            return this.requestQuery.sort;
        },
        sortDesc() {
            return this.requestQuery.desc;
        },
        page() {
            return this.requestQuery.number;
        },
        itemsPerPage() {
            return this.requestQuery.size;
        },
        filters() {
            return {
                ...this.filterQuery,
                q: this.dataSearchBarOptions.query
            };
        },
        displayTitle() {
            if (!isEmpty(this.selectedJob)) {
                return `Job tasks - ID:${this.selectedJob.id}`;
            }
            return 'Job status';
        },
        currentUserName() {
            if (!isEmpty(this.userInfo)) {
                return this.userInfo.username;
            }
            return '';
        },
        selectedJobId() {
            if (!isEmpty(this.selectedJob)) {
                return this.selectedJob.id;
            } else {
                return 0;
            }
        }
    },
    watch: {
        statusFilter(value) {
            let filters = {};
            if (value === 'COMPLETED') {
                filters = { username: this.currentUserName, status: value };
            } else {
                filters = { username: this.currentUserName };
            }
            this.findJobs(
                {
                    ...this.dataSearchBarOptions,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc,
                    query: ''
                },
                filters
            );
        }
    },
    async created() {
        if (this.tableFields && !this.tableFields.length) {
            await this.setupView();
        }
    },
    async mounted() {
        this.isDataLoading = true;
        await this.resetRequestQuery();
        setTimeout(() => {
            this.findJobs(this.getInitialPageParams(), {
                username: this.currentUserName
            });
        }, 200);
    },
    beforeDestroy() {
        this.stopAutoRefresh();
    },
    methods: {
        ...mapActions('jobs', ['fetchJobs', 'setupView', 'resetRequestQuery']),
        // =========================== Events ===============================
        onCloseClick() {
            this.stopAutoRefresh();
            this.$emit('close');
        },
        onDataSearchBarChange(options) {
            this.findJobs(
                {
                    ...options,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                this.filterQuery
            );
        },
        onUpdateSortBy(sortOptions) {
            if (sortOptions) {
                this.findJobs(
                    {
                        ...this.dataSearchBarOptions,
                        ...sortOptions
                    },
                    this.filterQuery
                );
            }
        },
        async onJobDetailsClick(item) {
            this.selectedJob = item;
            this.showDetails = true;
        },
        onCloseDetailsClick() {
            this.selectedJob = {};
            this.showDetails = false;
        },
        onEscapeKeydown() {
            this.onCloseClick();
        },
        // ===================== Others ========================
        async findJobs(searchOptions, filterOptions) {
            this.isDataLoading = true;
            try {
                if (!isEmpty(searchOptions)) {
                    const searchParams = {
                        number: searchOptions.page,
                        size: searchOptions.itemsPerPage,
                        q: searchOptions.query,
                        sort: searchOptions.sortBy,
                        desc: searchOptions.sortDesc
                    };
                    const filterParams = { ...filterOptions };
                    await this.fetchJobs({ searchParams, filterParams });
                    setTimeout(() => this.checkPendingJobs(), 200);
                }
            } finally {
                this.isDataLoading = false;
            }
        },
        refreshJobs() {
            this.findJobs(
                {
                    ...this.dataSearchBarOptions,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc,
                    query: ''
                },
                this.filterQuery
            );
        },
        getInitialPageParams() {
            return {
                page: 1,
                itemsPerPage: this.itemsPerPage,
                query: '',
                sortBy: this.sortBy,
                sortDesc: this.sortDesc
            };
        },
        getJobStatusColor(item) {
            if (item && item.errorCount > 0) {
                return 'error';
            } else if (item && item.successCount >= item.expectedTaskCount) {
                return 'success';
            } else if (item && item.successCount > 0 && item.errorCount > 0) {
                return 'warning';
            } else {
                return '';
            }
        },
        getJobStatusIcon(item) {
            if (item && item.successCount >= item.expectedTaskCount) {
                return this.icons.check;
            } else {
                return this.icons.error;
            }
        },
        checkPendingJobs() {
            let pending = 0;
            if (!isEmpty(this.jobs)) {
                pending = this.jobs.filter(
                    item => item.status === 'IN_PROGRESS'
                ).length;
            }
            if (pending > 0) {
                this.startAutoRefresh();
            } else {
                this.stopAutoRefresh();
            }
        },
        startAutoRefresh() {
            console.log('Starting jobs auto refresh', this.refreshTimer);
            if (!this.refreshTimer) {
                this.refreshTimer = setInterval(() => {
                    this.refreshJobs();
                }, 10000);
            }
        },
        stopAutoRefresh() {
            if (this.refreshTimer > 0) {
                console.log(
                    'Stopping the auto jobs refresh',
                    this.refreshTimer
                );
                clearInterval(this.refreshTimer);
                this.refreshTimer = null;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.job-status-icon {
    cursor: pointer;
}
</style>
